import React from 'react';
import styled from 'styled-components';
import {
  FieldScheduleEntry,
  ENTRY_TYPE_COLORS,
  ENTRY_TYPE_LABELS,
  formatTime
} from '../../types/schedule';

const Card = styled.div<{ $color: string }>`
  background: ${props => props.$color}15;
  border-left: 3px solid ${props => props.$color};
  border-radius: 4px;
  padding: 8px 10px;
  margin-bottom: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$color}25;
    transform: translateX(2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
`;

const CardTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
`;

const CardTime = styled.div`
  font-size: 11px;
  color: #8bb8d8;
  font-family: 'Orbitron', monospace;
  white-space: nowrap;
  margin-left: 8px;
`;

const CardMeta = styled.div`
  font-size: 11px;
  color: #8bb8d8;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 3px;
`;

const TypeBadge = styled.span<{ $color: string }>`
  font-size: 9px;
  padding: 2px 6px;
  border-radius: 3px;
  background: ${props => props.$color}30;
  color: ${props => props.$color};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const GameTypeBadge = styled.span`
  font-size: 9px;
  padding: 2px 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-weight: 500;
  text-transform: capitalize;
`;

interface ScheduleEntryCardProps {
  entry: FieldScheduleEntry;
  onClick?: (entry: FieldScheduleEntry) => void;
  compact?: boolean;
}

const ScheduleEntryCard: React.FC<ScheduleEntryCardProps> = ({
  entry,
  onClick,
  compact = false
}) => {
  const color = ENTRY_TYPE_COLORS[entry.entryType];
  const typeLabel = ENTRY_TYPE_LABELS[entry.entryType];

  const getTitle = () => {
    if (entry.entryType === 'referee_shift') {
      // Показываем имена рефери из массива или из одиночного поля
      const names: string[] = [];
      if (entry.refereeNames && entry.refereeNames.length > 0) {
        names.push(...entry.refereeNames);
      } else if (entry.refereeName) {
        names.push(entry.refereeName);
      }
      return names.length > 0 ? names.join(', ') : 'Unknown Referee';
    }
    return entry.title || 'Untitled Event';
  };

  const getRefereeNames = () => {
    const names: string[] = [];
    if (entry.refereeNames && entry.refereeNames.length > 0) {
      names.push(...entry.refereeNames);
    } else if (entry.refereeName) {
      names.push(entry.refereeName);
    }
    return names.join(', ');
  };

  const handleClick = () => {
    if (onClick) {
      onClick(entry);
    }
  };

  return (
    <Card $color={color} onClick={handleClick}>
      <CardHeader>
        <CardTitle>{getTitle()}</CardTitle>
        <CardTime>
          {formatTime(entry.startTime)} - {formatTime(entry.endTime)}
        </CardTime>
      </CardHeader>

      {!compact && (
        <CardMeta>
          <TypeBadge $color={color}>{typeLabel}</TypeBadge>

          {entry.entryType === 'referee_shift' && entry.gameType && (
            <GameTypeBadge>{entry.gameType}</GameTypeBadge>
          )}

          {/* Показываем рефери для events и private parties */}
          {entry.entryType !== 'referee_shift' && getRefereeNames() && (
            <MetaItem style={{ width: '100%', marginTop: '4px' }}>
              Referees: {getRefereeNames()}
            </MetaItem>
          )}

          {entry.description && (
            <MetaItem style={{ width: '100%', marginTop: '4px' }}>
              {entry.description.length > 50
                ? entry.description.substring(0, 50) + '...'
                : entry.description}
            </MetaItem>
          )}
        </CardMeta>
      )}
    </Card>
  );
};

export default ScheduleEntryCard;
